import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import { getClinicianPortalClientSnapshot } from "../api/portalRequestCache";
import {
  ActivityImageGallery,
  fileToBase64,
} from "../components/ActivityImages";
import {
  ActivityTypePill,
  BooleanIndicator,
  ContentSection,
  DisplayValue,
  HomeworkStatusPill,
  RecordDetailCard,
  RecordFieldRow,
  SudsPill,
} from "../components/DataDisplay";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import { DeleteHomeworkDialog } from "../components/HomeworkControls";
import { HomeworkForm } from "../components/HomeworkForm";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { ClientPrimaryTabs } from "../components/PortalNavigation";
import {
  clientWorkspaceBreadcrumbs,
  useClientWorkspace,
} from "../components/ClientWorkspace";
import { PortalLink } from "../components/PortalLink";
import { homeworkActivitiesReturnToFromSearch } from "../config/homeworkActivityNavigation";
import { homeworkStepsEditorPath } from "../config/activityStepsNavigation";
import { clientWorkspacePath } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import { formatPortalDate, formatPortalDateTime } from "../utils/dateTime";
import {
  navigateWithinPortal,
  replaceWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";
import { readTeamClinicId, withTeamClinicScope } from "../utils/teamClinicScope";

type HomeworkDetailContext =
  | {
      kind: "client";
      primaryKey: "current-homework" | "homework-history";
    }
  | { kind: "global" };

function deleteMutationFailure(authorized: boolean) {
  return new ClinicianPortalApiError(
    "The homework assignment could not be deleted.",
    authorized ? "UPSTREAM_ERROR" : "NOT_AUTHORIZED",
    authorized,
  );
}

function imageMutationFailure(
  action: "uploaded" | "deleted",
  authorized: boolean,
) {
  return new ClinicianPortalApiError(
    `The homework image could not be ${action}.`,
    authorized ? "UPSTREAM_ERROR" : "NOT_AUTHORIZED",
    authorized,
  );
}

function homeworkEditModeRequested(): boolean {
  return (
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("edit") === "1"
  );
}

export function HomeworkDetailPage({
  clinicianCode,
  patientId,
  homeworkAssignmentId,
  context,
}: {
  clinicianCode: string;
  patientId: string;
  homeworkAssignmentId: string;
  context: HomeworkDetailContext;
}) {
  const workspace = useClientWorkspace();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editing, setEditing] = useState(
    () => !workspace.readOnly && homeworkEditModeRequested(),
  );
  const [savedHomework, setSavedHomework] = useState<
    import("../api/contracts").PortalHomeworkDetail | null
  >(null);
  const request = useCallback(
    (signal: AbortSignal) =>
      workspace.accessScope === "team"
        ? clinicianPortalApi.getHomeworkDetail(
            clinicianCode,
            patientId,
            homeworkAssignmentId,
            signal,
            "team",
          )
        : clinicianPortalApi.getHomeworkDetail(
            clinicianCode,
            patientId,
            homeworkAssignmentId,
            signal,
          ),
    [clinicianCode, homeworkAssignmentId, patientId, workspace.accessScope],
  );
  const state = usePortalData(request);
  const snapshot = getClinicianPortalClientSnapshot(patientId, workspace.accessScope);
  const valuesRequest = useCallback(
    (signal: AbortSignal) =>
      workspace.accessScope === "team"
        ? clinicianPortalApi.getClientValues(
            clinicianCode,
            patientId,
            signal,
            "team",
          )
        : clinicianPortalApi.getClientValues(clinicianCode, patientId, signal),
    [clinicianCode, patientId, workspace.accessScope],
  );
  const mutation = usePortalMutation();
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist &&
      data.patient &&
      data.homework,
  );
  const patient = available ? data?.patient : snapshot?.patient;
  const therapist = available ? data?.therapist : snapshot?.therapist;
  const homework = available ? (savedHomework ?? data?.homework) : null;
  const valuesState = usePortalData(valuesRequest, {
    enabled: (editing && !workspace.readOnly) || Boolean(homework?.valueSupportedId),
  });
  const valueSupportedName = !homework?.valueSupportedId
    ? ""
    : valuesState.status === "loading"
      ? "Loading value…"
      : (
          valuesState.data?.found && valuesState.data.authorized
            ? valuesState.data.values.find(
                (value) => value.id === homework.valueSupportedId,
              )?.valueName
            : undefined
        ) || "Value unavailable";
  useEffect(() => {
    if (data?.homework && !editing && !savedHomework) {
      setSavedHomework(data.homework);
    }
  }, [data?.homework, editing, savedHomework]);
  const clientContext = context.kind === "client";
  const clientListKey = context.kind === "client" ? context.primaryKey : null;
  const clientListPath = clientListKey
    ? workspace.source === "team"
      ? withTeamClinicScope(
          clientWorkspacePath(patientId, clientListKey, "team"),
          readTeamClinicId(),
        )
      : clientWorkspacePath(patientId, clientListKey)
    : null;
  const clientListLabel =
    clientListKey === "homework-history"
      ? "Homework History"
      : clientListKey === "current-homework"
        ? "Current Homework"
        : null;
  const homeworkActivitiesReturnTo = homeworkActivitiesReturnToFromSearch(
    window.location.search,
  );
  const headerTitle = clientContext
    ? patient
      ? `Client Record: ${patient.accessCode} • ${patient.nickname}`
      : "Client Record"
    : homework?.homeworkName || "Homework Activity Detail";
  const breadcrumbItems = clientContext
    ? clientWorkspaceBreadcrumbs(
        workspace,
        patientId,
        patient?.nickname || "Client",
        homework?.homeworkName || "Homework Detail",
      )
    : [
        {
          label: "Homework Activities",
          path: homeworkActivitiesReturnTo,
        },
        { label: homework?.homeworkName || "Homework Detail" },
      ];
  const deleteAction = `delete:${homeworkAssignmentId}`;
  const isDeleting =
    mutation.state.status === "pending" &&
    mutation.state.action === deleteAction;
  const deleteFailed =
    mutation.state.status === "error" && mutation.state.action === deleteAction;
  const saveAction = `save:${homeworkAssignmentId}`;
  const isSaving =
    mutation.state.status === "pending" && mutation.state.action === saveAction;
  const saveFailed =
    mutation.state.status === "error" && mutation.state.action === saveAction;

  const exitEditMode = () => {
    setEditing(false);
    const search = new URLSearchParams(window.location.search);
    if (search.get("edit") !== "1") return;

    search.delete("edit");
    const query = search.toString();
    replaceWithinPortal(
      `${window.location.pathname}${query ? `?${query}` : ""}${
        window.location.hash
      }`,
    );
  };

  const saveHomework = async (
    fields: import("../api/contracts").HomeworkUpdateFields,
  ) => {
    if (!workspace.canManageHomework) return;
    const result = await mutation.run(saveAction, async (signal) => {
      const response = await clinicianPortalApi.updateHomework(
        clinicianCode,
        patientId,
        homeworkAssignmentId,
        fields,
        signal,
      );
      if (!response.success || !response.updated || !response.homework) {
        throw new ClinicianPortalApiError(
          "The Homework could not be saved.",
          response.authorized ? "UPSTREAM_ERROR" : "NOT_AUTHORIZED",
          response.authorized,
        );
      }
      return response.homework;
    });
    if (!result) return;
    setSavedHomework(result);
    exitEditMode();
    state.refresh();
  };

  const uploadHomeworkImage = async (file: File) => {
    if (!workspace.canManageHomework) {
      throw new ClinicianPortalApiError(
        "This supervised client record is read-only.",
        "NOT_AUTHORIZED",
        false,
      );
    }
    const response = await clinicianPortalApi.uploadHomeworkImage(
      clinicianCode,
      patientId,
      homeworkAssignmentId,
      {
        filename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        contentBase64: await fileToBase64(file),
      },
    );
    if (
      !response.success ||
      !response.uploaded ||
      !response.image ||
      response.homeworkAssignmentId !== homeworkAssignmentId
    ) {
      throw imageMutationFailure("uploaded", response.authorized);
    }

    const image = response.image;
    setSavedHomework((current) =>
      current
        ? {
            ...current,
            activityImages: [
              ...(current.activityImages ?? []).filter(
                (candidate) => candidate.id !== image.id,
              ),
              image,
            ],
          }
        : current,
    );
    return image;
  };

  const deleteHomeworkImage = async (imageId: string) => {
    if (!workspace.canManageHomework) {
      throw new ClinicianPortalApiError(
        "This supervised client record is read-only.",
        "NOT_AUTHORIZED",
        false,
      );
    }
    const response = await clinicianPortalApi.deleteHomeworkImage(
      clinicianCode,
      patientId,
      homeworkAssignmentId,
      imageId,
    );
    if (
      !response.success ||
      !response.deleted ||
      response.homeworkAssignmentId !== homeworkAssignmentId ||
      response.imageId !== imageId
    ) {
      throw imageMutationFailure("deleted", response.authorized);
    }

    setSavedHomework((current) =>
      current
        ? {
            ...current,
            activityImages: (current.activityImages ?? []).filter(
              (image) => image.id !== response.imageId,
            ),
          }
        : current,
    );
  };

  const deleteHomework = async () => {
    if (!workspace.canManageHomework) return;
    const result = await mutation.run(deleteAction, async (signal) => {
      const response = await clinicianPortalApi.deleteHomework(
        clinicianCode,
        patientId,
        homeworkAssignmentId,
        signal,
      );
      if (
        !response.success ||
        !response.deleted ||
        response.homeworkAssignmentId !== homeworkAssignmentId
      ) {
        throw deleteMutationFailure(response.authorized);
      }
      return response;
    });

    if (!result) return;
    const returnPath = clientContext
      ? `/clinician-portal/clients/${encodeURIComponent(patientId)}/${context.primaryKey}`
      : homeworkActivitiesReturnTo;
    navigateWithinPortal(withClinicianCode(returnPath, clinicianCode));
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey={clientContext ? (workspace.source === "team" ? "team" : "clients") : "homework"}
      clinicianCode={clinicianCode}
      therapistRole={therapist?.role}
    >
      <PortalHeader
        density={clientContext ? "compact" : "default"}
        title={headerTitle}
        clinicianName={therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs clinicianCode={clinicianCode} items={breadcrumbItems} />
        }
      >
        {clientContext ? (
          <ClientPrimaryTabs
            patientId={patientId}
            clinicianCode={clinicianCode}
            activeKey={context.primaryKey}
          />
        ) : null}
      </PortalHeader>
      <div
        className={`cp-page-content cp-page-content--profile cp-page-content--homework-detail${
          clientContext ? " cp-page-content--compact-client" : ""
        }`}
      >
        {state.status === "loading" ? (
          <LoadingState label="Loading homework detail" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Homework detail is not available")} />
        ) : null}
        {available && homework ? (
          <>
            {clientListPath && clientListLabel ? (
              <PortalLink
                className="cp-homework-detail__back-link"
                path={clientListPath}
                clinicianCode={clinicianCode}
                aria-label={`Back to ${clientListLabel}`}
              >
                ← Back to {clientListLabel}
              </PortalLink>
            ) : null}
            {editing && workspace.canManageHomework ? (
              <div className="cp-content-header cp-content-header--route-intro">
                <p>Update this assigned homework activity.</p>
                <div className="cp-content-header__actions">
                  <button
                    type="button"
                    className="cp-button cp-button--danger"
                    disabled={isDeleting}
                    onClick={() => {
                      mutation.reset();
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 aria-hidden="true" />
                    Delete
                  </button>
                </div>
              </div>
            ) : null}
            {editing ? (
              valuesState.status === "loading" ? (
                <LoadingState label="Loading client values" />
              ) : valuesState.status === "error" ? (
                <ErrorState
                  error={valuesState.error}
                  onRetry={valuesState.refresh}
                />
              ) : (
                <HomeworkForm
                  key={homework.updatedAt}
                  homework={homework}
                  values={valuesState.data?.values ?? []}
                  clinicianCode={clinicianCode}
                  patientId={patientId}
                  client={!clientContext && patient ? patient : undefined}
                  isSubmitting={isSaving}
                  submissionError={
                    saveFailed ? mutation.state.error : undefined
                  }
                  onUploadImage={uploadHomeworkImage}
                  onRemoveImage={deleteHomeworkImage}
                  onSubmit={saveHomework}
                  onCancel={() => {
                    mutation.reset();
                    exitEditMode();
                  }}
                />
              )
            ) : (
              <>
                <ContentSection
                  title="Homework Activity Details"
                  actions={workspace.canManageHomework ? (
                    <div className="cp-content-header__actions">
                      <button
                        type="button"
                        className="cp-button cp-button--primary"
                        onClick={() => {
                          mutation.reset();
                          setEditing(true);
                        }}
                      >
                        <Pencil aria-hidden="true" />
                        Edit Activity
                      </button>
                      <button
                        type="button"
                        className="cp-button cp-button--danger"
                        disabled={isDeleting}
                        onClick={() => {
                          mutation.reset();
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 aria-hidden="true" />
                        Delete
                      </button>
                    </div>
                  ) : undefined}
                >
                  <RecordDetailCard>
                    <RecordFieldRow label="Homework Activity">
                      <DisplayValue value={homework.homeworkName} />
                    </RecordFieldRow>
                    {!clientContext && patient ? (
                      <RecordFieldRow label="Client">
                        <PortalLink
                          path={`/clinician-portal/clients/${encodeURIComponent(patient.id)}/profile`}
                          clinicianCode={clinicianCode}
                        >
                          {patient.accessCode} • {patient.nickname}
                        </PortalLink>
                      </RecordFieldRow>
                    ) : null}
                    <RecordFieldRow label="Activity Type">
                      <ActivityTypePill activityType={homework.activityType} />
                    </RecordFieldRow>
                    <RecordFieldRow label="Value Supported">
                      <DisplayValue value={valueSupportedName} />
                    </RecordFieldRow>
                    <RecordFieldRow label="Exposure Homework">
                      <BooleanIndicator value={!homework.nonExposureHomework} />
                    </RecordFieldRow>
                    <RecordFieldRow label="Activity Steps">
                      <div className="cp-detail-value-with-action">
                        <span className="cp-multiline-value">
                          <DisplayValue value={homework.activitySteps} />
                        </span>
                        {workspace.canManageHomework ? <a
                          className="cp-edit-steps-button"
                          href={homeworkStepsEditorPath({
                            clinicianCode,
                            patientId,
                            homeworkAssignmentId,
                            returnUrl: window.location.href,
                          })}
                        >
                          Edit Steps
                        </a> : null}
                      </div>
                    </RecordFieldRow>
                    <RecordFieldRow label="Modification Steps">
                      <span className="cp-multiline-value">
                        <DisplayValue
                          value={
                            homework.alternativeSteps || homework.alternative
                          }
                        />
                      </span>
                    </RecordFieldRow>
                    <RecordFieldRow label="Coaching Prompts">
                      <span className="cp-multiline-value">
                        <DisplayValue value={homework.coachingPrompts} />
                      </span>
                    </RecordFieldRow>
                    <RecordFieldRow label="Activity Images">
                      <ActivityImageGallery images={homework.activityImages} />
                    </RecordFieldRow>
                    <RecordFieldRow label="Activity Points">
                      <DisplayValue value={homework.points} />
                    </RecordFieldRow>
                    <RecordFieldRow label="Activity Coins">
                      <DisplayValue value={homework.coins} />
                    </RecordFieldRow>
                    <RecordFieldRow label="Assigned Date">
                      {formatPortalDate(homework.assignedDate)}
                    </RecordFieldRow>
                    <RecordFieldRow label="Due Date">
                      {formatPortalDate(homework.dueDate)}
                    </RecordFieldRow>
                    {homework.dueDate ? (
                      <RecordFieldRow label="Repeats">
                        <DisplayValue
                          value={
                            homework.repeatsUi ||
                            homework.repeats ||
                            "Does not repeat"
                          }
                        />
                      </RecordFieldRow>
                    ) : null}
                    {homework.repeats === "Weekly" ? (
                      <RecordFieldRow label="Repeat On">
                        <DisplayValue value={homework.repeatOn.join(", ")} />
                      </RecordFieldRow>
                    ) : null}
                    {homework.isRecurring ? (
                      <RecordFieldRow label="Recurrence End Date">
                        {formatPortalDate(homework.recurrenceEndDate)}
                      </RecordFieldRow>
                    ) : null}
                  </RecordDetailCard>
                </ContentSection>
                <ContentSection title="Homework Outcome">
                  <RecordDetailCard>
                    <RecordFieldRow label="Overdue">
                      <BooleanIndicator value={homework.overdue} />
                    </RecordFieldRow>
                    <RecordFieldRow label="Email Alert">
                      <BooleanIndicator value={homework.emailAlert} />
                    </RecordFieldRow>
                    <RecordFieldRow label="Completion Status">
                      <HomeworkStatusPill status={homework.completionStatus} />
                    </RecordFieldRow>
                    <RecordFieldRow label="Completed Date and Time">
                      {formatPortalDateTime(homework.completedAt)}
                    </RecordFieldRow>
                    <RecordFieldRow label="Summary">
                      <span className="cp-multiline-value">
                        <DisplayValue value={homework.summary} />
                      </span>
                    </RecordFieldRow>
                    <RecordFieldRow label="Pre-Exposure SUDS">
                      <SudsPill value={homework.firstSuds} />
                    </RecordFieldRow>
                    <RecordFieldRow label="Post-Exposure SUDS">
                      <SudsPill value={homework.secondSuds} />
                    </RecordFieldRow>
                    <RecordFieldRow label="Sentiment Analysis">
                      <span className="cp-multiline-value">
                        <DisplayValue value={homework.sentimentAnalysis} />
                      </span>
                    </RecordFieldRow>
                    <RecordFieldRow label="Coping Skills Used">
                      <span className="cp-multiline-value">
                        <DisplayValue value={homework.copingSkills} />
                      </span>
                    </RecordFieldRow>
                    <RecordFieldRow label="Steps Altered">
                      <span className="cp-multiline-value">
                        <DisplayValue value={homework.altered} />
                      </span>
                    </RecordFieldRow>
                    <RecordFieldRow label="Routine Time">
                      <DisplayValue value={homework.routineTime} />
                    </RecordFieldRow>
                    <RecordFieldRow label="Client Uploads">
                      <ActivityImageGallery
                        images={homework.clientUploads ?? []}
                      />
                    </RecordFieldRow>
                    <RecordFieldRow label="Conversation">
                      {homework.conversationReportUrl ? (
                        <a
                          className="cp-conversation-link"
                          href={homework.conversationReportUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Log
                        </a>
                      ) : (
                        <span className="cp-empty-value">Not available</span>
                      )}
                    </RecordFieldRow>
                    <RecordFieldRow label="Created">
                      {formatPortalDateTime(homework.createdAt)}
                    </RecordFieldRow>
                  </RecordDetailCard>
                </ContentSection>
              </>
            )}
            {workspace.canManageHomework ? <DeleteHomeworkDialog
              homeworkName={homework.homeworkName}
              isOpen={deleteDialogOpen}
              isDeleting={isDeleting}
              hasError={deleteFailed}
              onCancel={() => {
                setDeleteDialogOpen(false);
                mutation.reset();
              }}
              onConfirm={deleteHomework}
            /> : null}
          </>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
